// Products — every store, one table, read and written here.
//
// Three rules this screen exists to hold:
//   1. Product information lives in ONE place. There is no per-platform branch
//      here and no "if this store has none, look somewhere else" — a store with
//      no rows genuinely has no product data, and says so.
//   2. Everything the shop sent is reachable by turning on a column. There is no
//      second route to the data and no button that shows what the columns do not;
//      the payload fields (all 231 of WGR's, MyCashflow's variation and
//      stock_item, the Shopify variant) are offered in the picker like any other.
//   3. What can be edited is decided by the server, from the same list the
//      column picker is built from. A column cannot be editable on screen while a
//      sync quietly overwrites it an hour later.
//
// The catalogue comes from /api/products/columns, so a column added to
// wms_products becomes visible — and editable if it should be — without touching
// this file.                                            (Claude, 2026-08-27)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authFetch, API } from './api';
import { fmtWh } from './time';
import { PhoneCards, CardLine } from './PhoneCards';
import CameraScanner from './products/CameraScanner';
import { acceptCode } from './products/acceptCode';
import { lookup as scanLookup } from './products/scanApi';
import { scanSound } from './scanSound';

// Which stores an edit of a column is written back to (products_schema.
// PLATFORM_FIELD_SYNC, served per column by /api/products/columns), or why it is
// WMS-only. The screen marks what syncs from the same matrix the relays send
// from, so it can never claim a sync that does not happen. (Fernando, 2026-09-08)
interface ColumnSync { platform: string; field: string; resource: string; on: boolean; store: string; }
interface Column {
  key: string; label: string; type: string;
  default: boolean; editable: boolean; group: string;
  syncs?: Record<string, ColumnSync>;
  wms_only?: string;
}
// the newest write-back of one cell (rows[].push_status[field])
interface PushInfo {
  id: number; platform_field: string; status: string; error: string | null;
  attempts: number; value: string | null; at: string | null; next_at: string | null;
}
interface Push extends PushInfo {
  field: string; resource: string; username: string; target_identifier: string | null;
  created_at: string | null; settled_at: string | null; next_attempt_at: string | null;
}
interface Webshop { id: string; name: string; platform: string; }
interface Event {
  id: number; field: string; old_value: string | null;
  new_value: string | null; username: string; created_at: string | null;
}

const PREF_KEY = 'wms_product_columns';
const PER_PAGE = 100;

export default function ProductsTable({ webshopId: fixedId }: { webshopId?: string } = {}) {
  const [columns, setColumns] = useState<Column[]>([]);
  const [visible, setVisible] = useState<string[] | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [webshops, setWebshops] = useState<Webshop[]>([]);
  const [shop, setShop] = useState(fixedId || '');
  // the last search survives a trip to a product page and back (layer 2 → layer 1, Fernando 31.10)
  const [search, setSearch] = useState(() => { try { return (!fixedId && sessionStorage.getItem('wms_products_search')) || ''; } catch { return ''; } });
  const [q, setQ] = useState(search.trim());
  // the camera (Products › Scan P1): a read lands in the search box like a typed code; ?camera-test=1 only shows what it read
  const location = useLocation();
  const cameraTest = !fixedId && new URLSearchParams(location.search).get('camera-test') === '1';
  const [camera, setCamera] = useState(cameraTest);
  // "every size of the found product": the search's hit widened to its whole style in every shop (one lookup, then ids=)
  const [allSizes, setAllSizes] = useState(false);
  const [ids, setIds] = useState<number[] | null>(null);
  const [idsNote, setIdsNote] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState('article_number');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [colFilter, setColFilter] = useState('');

  const [editing, setEditing] = useState<{ id: number; key: string } | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkField, setBulkField] = useState('stock_location');
  const [bulkValue, setBulkValue] = useState('');
  const [history, setHistory] = useState<{ row: any; events: Event[]; pushes: Push[] } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const role = localStorage.getItem('wms_role') || 'staff';
  const canRetry = role === 'admin' || role === 'manager';

  // the catalogue, and this person's own choice of what to show
  useEffect(() => {
    const p = shop ? `?webshop_id=${encodeURIComponent(shop)}` : '';
    authFetch(`${API}/products/columns${p}`).then(r => r.json()).then(d => {
      const cols: Column[] = d.columns || [];
      setColumns(cols);
      setCanEdit(!!d.can_edit);
      setVisible(prev => {
        if (prev) return prev;                       // a choice already made stands
        let saved: string[] | null = null;
        try { saved = JSON.parse(localStorage.getItem(PREF_KEY) || 'null'); } catch { /* ignore */ }
        const valid = saved?.filter(k => cols.some(c => c.key === k));
        return valid?.length ? valid : cols.filter(c => c.default).map(c => c.key);
      });
    });
  }, [shop]);

  useEffect(() => {
    if (!fixedId) authFetch(`${API}/settings/webshops`).then(r => r.ok ? r.json() : []).then(setWebshops);
  }, [fixedId]);

  useEffect(() => {
    if (visible) localStorage.setItem(PREF_KEY, JSON.stringify(visible));
  }, [visible]);

  useEffect(() => {
    const t = setTimeout(() => { setQ(search.trim()); setPage(1); }, 300);
    try { if (!fixedId) sessionStorage.setItem('wms_products_search', search); } catch { /* ignore */ }
    return () => clearTimeout(t);
  }, [search, fixedId]);

  // the whole style of the found product: ask /api/products/scan once, then list exactly those rows
  useEffect(() => {
    let dead = false;
    if (!allSizes || !q || !acceptCode(q)) { setIds(null); setIdsNote(''); return; }
    // union: a code two shops sell (a Nora and a Motley garment) or two products share → every style, no question back
    scanLookup(q, { union: true }).then(r => {
      if (dead) return;
      if (r.status === 'ok') { setIds((r.groups || []).flatMap(g => g.sizes.map(s => s.wms_id))); setIdsNote(''); }
      else { setIds(null); setIdsNote('No product with that code — showing the plain search.'); }
    }).catch(() => { if (!dead) { setIds(null); setIdsNote(''); } });
    return () => { dead = true; };
  }, [allSizes, q]);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const shown = useMemo(
    () => columns.filter(c => visible?.includes(c.key)),
    [columns, visible]);

  // only payload columns need asking for by name; the native ones always come back
  const extras = useMemo(
    () => shown.filter(c => c.key.startsWith('raw.')).map(c => c.key).join(','),
    [shown]);

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE), sort, direction: dir });
    if (shop) p.set('webshop_id', shop);
    if (ids) p.set('ids', ids.join(','));
    else if (q) p.set('search', q);
    if (extras) p.set('extra', extras);
    const r = await authFetch(`${API}/products/v2?${p}`);
    const d = await r.json();
    setRows(d.products || []); setTotal(d.total || 0); setPages(d.pages || 1);
    setLoading(false);
  }, [shop, q, ids, page, sort, dir, extras]);
  useEffect(() => { load(); }, [load]);

  const fmt = (v: any, type: string) => {
    if (v === null || v === undefined || v === '') return <span className="text-gray-700">—</span>;
    if (type === 'bool') return v ? 'yes' : 'no';
    if (type === 'money') return Number(v).toFixed(2);
    if (type === 'json') return Array.isArray(v) ? (v.join(', ') || <span className="text-gray-700">—</span>) : String(v);
    if (type === 'time') return String(v).replace('T', ' ').slice(0, 16);
    return String(v);
  };

  const beginEdit = (row: any, col: Column) => {
    if (!canEdit || !col.editable) return;
    setEditing({ id: row.wms_id, key: col.key });
    const v = row[col.key];
    setDraft(v === null || v === undefined ? '' : String(v));
  };

  const commit = async () => {
    if (!editing) return;
    const cell = editing;                 // captured: the await below can outlive it
    const row = rows.find(r => r.wms_id === cell.id);
    const before = row?.[cell.key];
    if (String(before ?? '') === draft.trim()) { setEditing(null); return; }
    setSaving(true);
    const r = await authFetch(`${API}/products/v2/${cell.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [cell.key]: draft }),
    });
    setSaving(false);
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      setNotice(e.detail || 'That change was not saved.');
      return;                                  // stay in the cell; nothing is lost
    }
    const d = await r.json();
    // a shelf edit moves the other shop's row of the same piece too (Motley ↔ Big Oliver, 5.11) — those rows follow here
    const twins: { wms_id: number; webshop_id: string; field?: string; value?: string | null; error?: string }[] = d.twins || [];
    setRows(rs => rs.map(x => {
      if (x.wms_id === cell.id) return { ...x, ...d.product };
      const tw = twins.filter(t => t.wms_id === x.wms_id && t.field);
      return tw.length ? tw.reduce((acc, t) => ({ ...acc, [t.field as string]: t.value }), { ...x }) : x;
    }));
    setEditing(null);
    // say where the edit is going — or that it stays here
    const col = columns.find(c => c.key === cell.key);
    const q = (d.queued || [])[0];
    const shopOf = (id: string) => webshops.find(w => w.id === id)?.name || id;
    const moved = Array.from(new Set(twins.filter(t => !t.error).map(t => shopOf(t.webshop_id))));
    const failed = Array.from(new Set(twins.filter(t => t.error).map(t => shopOf(t.webshop_id))));
    const twinText = (moved.length ? ` The ${moved.join(' and ')} row follows.` : '')
      + (failed.length ? ` The ${failed.join(' and ')} row could not be changed — set it there by hand.` : '');
    if (q) {
      setNotice(`Saved. Sending ${col?.label || cell.key} to ${shopOf(q.webshop_id)} as ${q.platform_field}…${twinText}`);
    } else {
      setNotice(twinText ? `Saved.${twinText}` : null);
    }
  };

  const retryPush = async (p: PushInfo) => {
    if (!window.confirm('Send this value to the shop again?')) return;
    const r = await authFetch(`${API}/products/pushes/${p.id}/retry`, { method: 'POST' });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      setNotice(e.detail || 'Could not retry.');
      return;
    }
    setNotice('Queued again — it goes out within a minute.');
    load();
  };

  // "↔ Motley Denim: picklistReference" for a column, for the store(s) in view
  const syncText = (c: Column): string | null => {
    const entries = Object.entries(c.syncs || {}).filter(([id]) => !shop || id === shop);
    if (!entries.length) return null;
    return entries.map(([, s]) => `${s.store}: ${s.field}${s.on ? '' : ' (write-back off)'}`).join(' · ');
  };
  const syncOn = (c: Column): boolean =>
    Object.entries(c.syncs || {}).some(([id, s]) => (!shop || id === shop) && s.on);

  // what happens to THIS cell when it is edited — depends on the row's store,
  // not only the column (Picklist # goes to WGR for a Motley row, nowhere for a
  // Big Oliver row). Shown on hover of every editable cell.
  const cellSyncTitle = (c: Column, r: any, storeName: string, p?: PushInfo): string | undefined => {
    if (!c.editable) return undefined;
    const s = (c.syncs || {})[r.webshop_id];
    const owned = (r.owned_fields || []).includes(c.key)
      ? 'Set in the WMS — syncs will not overwrite this. ' : '';
    if (!s) {
      return `${owned}Stays in the WMS: ${storeName} (${String(r.platform || '').toUpperCase()}) has no field for ${c.label}`
        + (c.wms_only ? ` — ${c.wms_only}` : '') + '.';
    }
    const sent = p?.status === 'ok' ? ` Last sent ${p.at ? fmtWh(p.at, 'short') : ''}.` : '';
    return s.on
      ? `${owned}An edit is written to ${storeName} as ${s.field}.${sent}`
      : `${owned}Would be written to ${storeName} as ${s.field}, but write-back is switched OFF for this store (Settings → Stores).${sent}`;
  };

  const pushMark = (p?: PushInfo, storeName?: string) => {
    if (!p || p.status === 'superseded' || p.status === 'dismissed') return null;
    const when = p.at ? fmtWh(p.at, 'short') : '';
    if (p.status === 'ok') {
      return <span className="ml-1 text-green-400" title={`Sent to ${storeName || 'the shop'} ${when} as ${p.platform_field}`}>✓</span>;
    }
    if (p.status === 'pending' || p.status === 'sending') {
      const t = p.attempts > 0
        ? `Retry ${p.attempts + 1} ${p.next_at ? 'at ' + fmtWh(p.next_at, 'time') : 'soon'} — ${p.error || ''}`
        : `Sending to ${storeName || 'the shop'}…`;
      return <span className="ml-1 text-gray-400" title={t}>⟳</span>;
    }
    // failed / unsupported
    return (
      <span className="ml-1 text-red-400 cursor-pointer"
        title={`Not sent to ${storeName || 'the shop'}: ${p.error || p.status}${canRetry ? ' — click to retry' : ''}`}
        onClick={e => { e.stopPropagation(); if (canRetry) retryPush(p); }}>✗</span>
    );
  };

  const runBulk = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const col = columns.find(c => c.key === bulkField);
    if (!window.confirm(
      `Set ${col?.label || bulkField} to "${bulkValue}" on ${ids.length} product${ids.length > 1 ? 's' : ''}?`)) return;
    setSaving(true);
    const r = await authFetch(`${API}/products/v2/bulk-edit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wms_ids: ids, fields: { [bulkField]: bulkValue } }),
    });
    setSaving(false);
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      setNotice(e.detail || 'The bulk change was not applied.');
      return;
    }
    const d = await r.json();
    const failed: any[] = d.twins_failed || [];
    setNotice(`${d.updated} of ${d.requested} products changed.`
      + (d.twins ? ` ${d.twins} row${d.twins > 1 ? 's' : ''} of the other shop (Motley ↔ Big Oliver) took the same shelf.` : '')
      + (failed.length ? ` ${failed.length} of those could not be changed: ${failed.map(t => t.article_number || t.wms_id).join(', ')}.` : '')
      + (d.queued
        ? ` ${d.queued} change${d.queued > 1 ? 's' : ''} queued for the shop, sent at about ${d.per_minute} a minute.`
        : ''));
    setSelected(new Set());
    setBulkValue('');
    load();
  };

  // Pulling from the shop is per store, because that is what a sync is. WGR's
  // catalogue is 73k articles fetched one at a time, so that one answers
  // "started" rather than "done" and the message says so.
  const syncNow = async () => {
    setSyncing(true);
    const r = await authFetch(`${API}/sync-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ webshop_id: shop }),
    });
    const d = await r.json().catch(() => ({}));
    setSyncing(false);
    if (!r.ok) { setNotice(d.detail || 'The sync could not be started.'); return; }
    if (d.message) setNotice(d.message);
    else {
      setNotice(`Synced ${d.synced_variations ?? 0} variants from the shop.`);
      load();
    }
  };

  const toggleCol = (key: string) =>
    setVisible(v => v!.includes(key) ? v!.filter(k => k !== key) : [...v!, key]);

  const toggleRow = (id: number) =>
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const sortBy = (key: string) => {
    if (key.startsWith('raw.')) return;   // payload fields are not indexed to sort on
    if (sort === key) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(key); setDir('asc'); }
    setPage(1);
  };

  const grouped = useMemo(() => {
    const f = colFilter.trim().toLowerCase();
    const out: Record<string, Column[]> = {};
    for (const c of columns) {
      if (f && !c.label.toLowerCase().includes(f) && !c.key.toLowerCase().includes(f)) continue;
      (out[c.group] = out[c.group] || []).push(c);
    }
    return out;
  }, [columns, colFilter]);

  const editableCols = columns.filter(c => c.editable);

  return (
    <div className="p-3 md:p-6">
      <div className="flex flex-wrap items-start justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-sm text-gray-500 mt-1">
            Every store's product data in one place — {total.toLocaleString()} variants.
            Each has a WMS ID of its own; the store's own ID is kept beside it.
            {canEdit && ' Click a highlighted cell to change it.'}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            <span className="text-emerald-400/80">↔</span> edits are sent to the shop ·{' '}
            <span className="text-gray-600">↔</span> the shop has a field for it, but write-back is switched off
            for that store (Settings → Stores) · no ↔ = stays in the WMS.
            Hover a column or a cell to see the store and the field name; a cell shows{' '}
            <span className="text-gray-400">⟳</span> while sending, <span className="text-green-400">✓</span> when the shop confirmed,{' '}
            <span className="text-red-400">✗</span> when it refused.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!fixedId && (
            <button onClick={() => setCamera(true)}
              title="read a barcode with the camera — it lands in the search box (2026-10-31)"
              className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 hover:bg-blue-500 text-white">
              📷 Scan
            </button>
          )}
          {canEdit && shop && (
            <button onClick={syncNow} disabled={syncing}
              className="px-3 py-1.5 text-sm rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 disabled:opacity-40">
              {syncing ? 'Syncing…' : 'Sync from shop'}
            </button>
          )}
          <button onClick={() => setPickerOpen(o => !o)}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200">
            Columns ({shown.length}/{columns.length})
          </button>
        </div>
      </div>

      {notice && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-200 flex justify-between">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-gray-500 hover:text-white">✕</button>
        </div>
      )}

      {pickerOpen && (
        <div className="mb-4 bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
          <div className="flex flex-wrap items-center justify-between mb-3 gap-3">
            <p className="text-xs text-gray-500">
              Choose what stays on screen. Everything is stored either way — this only
              decides what is shown, and your choice is remembered on this browser.
              The <span className="text-gray-400">WMS</span> group is what the warehouse
              uses; the others are every field the shop itself sent.
              <span className="text-blue-500/70"> ✎</span> = you can edit it;
              <span className="text-emerald-400/80"> ↔</span> = an edit is written back to the shop
              (hover to see which store and field; grey ↔ = the store has write-back switched off).
            </p>
            <div className="flex gap-2 shrink-0">
              <input value={colFilter} onChange={e => setColFilter(e.target.value)}
                placeholder="Find a field…"
                className="w-40 bg-gray-800 border border-gray-700 text-gray-200 text-xs rounded px-2 py-1 focus:outline-none focus:border-blue-500" />
              <button onClick={() => setVisible(columns.filter(c => c.default).map(c => c.key))}
                className="px-2 py-1 text-xs rounded bg-gray-800 hover:bg-gray-700 text-gray-300">
                Daily set
              </button>
              <button onClick={() => setVisible(columns.map(c => c.key))}
                className="px-2 py-1 text-xs rounded bg-gray-800 hover:bg-gray-700 text-gray-300">
                Show all
              </button>
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto pr-1">
            {Object.entries(grouped).map(([group, cols]) => (
              <div key={group} className="mb-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{group}</span>
                  <span className="text-xs text-gray-600">{cols.length}</span>
                  <button onClick={() => setVisible(v => Array.from(new Set([...v!, ...cols.map(c => c.key)])))}
                    className="text-xs text-blue-400 hover:underline">all</button>
                  <button onClick={() => setVisible(v => v!.filter(k => !cols.some(c => c.key === k)))}
                    className="text-xs text-blue-400 hover:underline">none</button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                  {cols.map(c => (
                    <label key={c.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={visible?.includes(c.key) || false}
                        onChange={() => toggleCol(c.key)} className="accent-blue-500" />
                      <span className={`truncate ${visible?.includes(c.key) ? 'text-gray-200' : 'text-gray-500'}`}
                        title={syncText(c) ? `${c.key} — written back to ${syncText(c)}`
                          : c.wms_only ? `${c.key} — WMS only: ${c.wms_only}` : c.key}>
                        {c.label}{c.editable && <span className="text-blue-500/70"> ✎</span>}
                        {syncText(c) && <span className={syncOn(c) ? 'text-emerald-400/80' : 'text-gray-600'}> ↔</span>}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!fixedId && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button onClick={() => { setShop(''); setPage(1); }}
            className={`px-3 py-1 text-xs rounded-full font-medium ${!shop ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            All stores
          </button>
          {webshops.map(w => (
            <button key={w.id} onClick={() => { setShop(w.id); setPage(1); }}
              className={`px-3 py-1 text-xs rounded-full font-medium ${shop === w.id ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
              {w.name} <span className="opacity-50">{w.platform}</span>
            </button>
          ))}
        </div>
      )}

      {camera && (
        <CameraScanner lang="en" testMode={cameraTest} onClose={() => setCamera(false)}
          onScan={code => { const c = acceptCode(code); if (c) { setSearch(c); scanSound('ok'); } }} />
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, article #, picklist #, barcode, location, style, commodity code…"
          className="w-full max-w-2xl bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500" />
        {!fixedId && (
          <label className="inline-flex items-center gap-2 text-sm text-gray-300 whitespace-nowrap" title="a barcode, article or picklist number found → its whole style in every shop">
            <input type="checkbox" checked={allSizes} onChange={e => setAllSizes(e.target.checked)} />
            Every size of the found product
          </label>
        )}
        {idsNote && <span className="text-xs text-amber-300">{idsNote}</span>}
        {ids && <span className="text-xs text-gray-400">{ids.length} rows of this product</span>}
      </div>

      {canEdit && selected.size > 0 && (
        <div className="mb-3 flex items-center gap-2 flex-wrap bg-[#1a1d27] border border-blue-900/50 rounded-xl px-3 py-2">
          <span className="text-sm text-gray-300">{selected.size} selected —</span>
          <span className="text-sm text-gray-500">set</span>
          <select value={bulkField} onChange={e => setBulkField(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1">
            {editableCols.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <span className="text-sm text-gray-500">to</span>
          <input value={bulkValue} onChange={e => setBulkValue(e.target.value)}
            placeholder="(blank clears it)"
            className="bg-gray-800 border border-gray-700 text-gray-200 text-sm rounded px-2 py-1 w-full sm:w-48" />
          <button onClick={runBulk} disabled={saving}
            className="px-3 py-1 text-sm rounded bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-40">
            Apply to {selected.size}
          </button>
          <button onClick={() => setSelected(new Set())}
            className="px-2 py-1 text-sm text-gray-400 hover:text-white">Clear</button>
        </div>
      )}

      {/* phone: a card per product — what a stock lookup on the floor needs; tap = the product page (layer 2), History stays a link */}
      <PhoneCards rows={loading ? [] : rows} keyOf={r => r.wms_id}
        card={r => (
          <div onClick={() => navigate(`/products/${r.wms_id}`)}>
            <div className="text-white font-medium leading-snug">{r.name || r.article_number || r.wms_id}</div>
            <div className="text-xs text-gray-400 mb-1">{[r.article_number, r.size, r.picklist_number].filter(Boolean).join(' · ')}</div>
            <CardLine label="Location"><span className="text-blue-300 font-mono">{r.stock_location || '—'}</span></CardLine>
            <CardLine label="Stock">{r.stock_qty ?? '—'}</CardLine>
            {r.barcode && <CardLine label="Barcode"><span className="font-mono">{r.barcode}</span></CardLine>}
            <CardLine label="Store">{webshops.find(w => w.id === r.webshop_id)?.name || r.webshop_id}</CardLine>
            <div className="mt-1 text-right">
              <button onClick={e => { e.stopPropagation(); Promise.all([
                  authFetch(`${API}/products/v2/${r.wms_id}/events`).then(x => x.json()),
                  authFetch(`${API}/products/v2/${r.wms_id}/pushes`).then(x => x.ok ? x.json() : []),
                ]).then(([events, pushes]) => setHistory({ row: r, events, pushes })); }}
                className="text-xs text-blue-400 hover:underline">History</button>
            </div>
          </div>
        )}>
      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              {canEdit && (
                <th className="px-3 py-2 w-8">
                  <input type="checkbox" className="accent-blue-500"
                    checked={rows.length > 0 && rows.every(r => selected.has(r.wms_id))}
                    onChange={e => setSelected(e.target.checked
                      ? new Set(rows.map(r => r.wms_id)) : new Set())} />
                </th>
              )}
              {shown.map(c => (
                <th key={c.key} onClick={() => sortBy(c.key)}
                  title={syncText(c) ? `${c.key} — edits are written back to ${syncText(c)}`
                    : c.wms_only ? `${c.key} — WMS only: ${c.wms_only}` : c.key}
                  className={`px-3 py-2 text-left font-medium whitespace-nowrap ${
                    c.key.startsWith('raw.') ? '' : 'cursor-pointer hover:text-gray-300'}`}>
                  {c.label}
                  {syncText(c) && <span className={`ml-1 ${syncOn(c) ? 'text-emerald-400/80' : 'text-gray-600'}`}>↔</span>}
                  {sort === c.key && <span className="ml-1">{dir === 'asc' ? '↑' : '↓'}</span>}
                </th>
              ))}
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={shown.length + 2} className="text-center py-10 text-gray-500">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={shown.length + 2} className="text-center py-12 text-gray-500">
                {q ? 'Nothing matches that search.' : 'This store has no product data in the WMS.'}
              </td></tr>
            )}
            {!loading && rows.map(r => {
              const owned: string[] = r.owned_fields || [];
              const pushes: Record<string, PushInfo> = r.push_status || {};
              const storeName = webshops.find(w => w.id === r.webshop_id)?.name || r.webshop_id;
              return (
                <tr key={r.wms_id} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                  {canEdit && (
                    <td className="px-3 py-2">
                      <input type="checkbox" className="accent-blue-500"
                        checked={selected.has(r.wms_id)} onChange={() => toggleRow(r.wms_id)} />
                    </td>
                  )}
                  {shown.map(c => {
                    const isEditing = editing?.id === r.wms_id && editing?.key === c.key;
                    const canTouch = canEdit && c.editable;
                    return (
                      <td key={c.key}
                        onClick={() => !isEditing && beginEdit(r, c)}
                        title={cellSyncTitle(c, r, storeName, pushes[c.key])}
                        className={`px-3 py-2 whitespace-nowrap max-w-xs truncate ${
                          owned.includes(c.key) ? 'text-blue-300' : 'text-gray-300'} ${
                          canTouch ? 'cursor-text hover:bg-blue-500/10' : ''}`}>
                        {isEditing ? (
                          <input ref={inputRef} value={draft} disabled={saving}
                            onChange={e => setDraft(e.target.value)}
                            onBlur={commit}
                            onKeyDown={e => {
                              if (e.key === 'Enter') commit();
                              if (e.key === 'Escape') { setEditing(null); setNotice(null); }
                            }}
                            className="w-full bg-gray-900 border border-blue-500 text-gray-100 text-sm rounded px-1 py-0.5 focus:outline-none" />
                        ) : <>{fmt(r[c.key], c.type)}{pushMark(pushes[c.key], storeName)}</>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 whitespace-nowrap">
                    {/* layer 2: this product alone — every size, every shop, editable, movable (2026-10-31) */}
                    <button onClick={() => navigate(`/products/${r.wms_id}`)} className="text-xs text-blue-400 hover:underline mr-3">Open</button>
                    <button onClick={() => Promise.all([
                        authFetch(`${API}/products/v2/${r.wms_id}/events`).then(x => x.json()),
                        authFetch(`${API}/products/v2/${r.wms_id}/pushes`).then(x => x.ok ? x.json() : []),
                      ]).then(([events, pushes]) => setHistory({ row: r, events, pushes }))}
                      className="text-xs text-blue-400 hover:underline">History</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </PhoneCards>

      {pages > 1 && (
        <div className="flex items-center gap-3 mt-3 text-sm text-gray-400">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-2 py-1 rounded bg-gray-800 disabled:opacity-30">Previous</button>
          <span>Page {page} of {pages.toLocaleString()}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
            className="px-2 py-1 rounded bg-gray-800 disabled:opacity-30">Next</button>
        </div>
      )}

      {history && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-2 md:p-8 z-50"
             onClick={() => setHistory(null)}>
          <div className="bg-[#1a1d27] border border-gray-800 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto p-5"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-white font-semibold">{history.row.name || history.row.article_number}</h3>
                <p className="text-xs text-gray-500">
                  WMS ID {history.row.wms_id} · {history.row.webshop_id} · every change made in the WMS
                </p>
              </div>
              <button onClick={() => setHistory(null)} className="text-gray-500 hover:text-white">✕</button>
            </div>
            {history.events.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">
                Nobody has changed this product in the WMS. Everything on it came from the shop.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-xs border-b border-gray-800">
                    <th className="text-left py-1.5">When</th>
                    <th className="text-left">Who</th>
                    <th className="text-left">Field</th>
                    <th className="text-left">From</th>
                    <th className="text-left">To</th>
                  </tr>
                </thead>
                <tbody>
                  {history.events.map(e => (
                    <tr key={e.id} className="border-b border-gray-800/50 text-gray-300">
                      <td className="py-1.5 whitespace-nowrap text-gray-500">
                        {(e.created_at || '').replace('T', ' ').slice(0, 16)}
                      </td>
                      <td>{e.username}</td>
                      <td>{columns.find(c => c.key === e.field)?.label || e.field}</td>
                      <td className="text-gray-500">{e.old_value ?? '—'}</td>
                      <td>{e.new_value ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {history.pushes.length > 0 && (
              <>
                <h4 className="text-gray-300 font-medium mt-6 mb-2 text-sm">Sent to the shop</h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-xs border-b border-gray-800">
                      <th className="text-left py-1.5">When</th>
                      <th className="text-left">Field</th>
                      <th className="text-left">Value</th>
                      <th className="text-left">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.pushes.map(p => (
                      <tr key={p.id} className="border-b border-gray-800/50 text-gray-300">
                        <td className="py-1.5 whitespace-nowrap text-gray-500">
                          {fmtWh(p.settled_at || p.created_at, 'short')}
                        </td>
                        <td>{columns.find(c => c.key === p.field)?.label || p.field}
                          <span className="text-gray-600"> → {p.platform_field}</span></td>
                        <td>{p.value ?? '—'}</td>
                        <td className={p.status === 'ok' ? 'text-green-400'
                          : p.status === 'failed' || p.status === 'unsupported' ? 'text-red-400' : 'text-gray-400'}>
                          {p.status === 'ok' ? 'sent' : p.status === 'pending' && p.attempts > 0 ? `retrying (${p.attempts})` : p.status}
                          {p.error && <span className="text-gray-500"> — {p.error}</span>}
                          {(p.status === 'failed' || p.status === 'unsupported') && canRetry && (
                            <button onClick={() => retryPush(p)} className="ml-2 text-xs text-blue-400 hover:underline">Retry</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
